import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './OnboardWizard.css';

// Import step components
import WelcomeStep from './add-channel/WelcomeStep';
import ConfigureChannelStep from './add-channel/ConfigureChannelStep';
import ConfirmStep from './add-channel/ConfirmStep';
import CompleteStep from './add-channel/CompleteStep';

// Define wizard steps
type WizardStep = 'welcome' | 'configure' | 'confirm' | 'complete';

interface WizardState {
  currentStep: WizardStep;
  protocol: 'a2a' | 'ap2' | 'mcp' | null;
  channelConfig: any;
  // Store configure step form state to preserve across navigation
  configureFormState: {
    selectedHostPort: string;
    selectedPrefix: string;
    customPath: string;
    name: string;
    description: string;
    target_endpoint: string;
    mediator_id: string;
  } | null;
}

const AddChannelWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { actions, getCurrentStats } = useApp();
  const [searchParams] = useSearchParams();
  
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'welcome',
    protocol: null,
    channelConfig: null,
    configureFormState: null,
  });

  // Check for protocol in URL params and skip to configure step if provided
  useEffect(() => {
    const protocolParam = searchParams.get('protocol');
    const extensionParam = searchParams.get('extension');
    
    if (protocolParam && ['a2a', 'ap2', 'mcp'].includes(protocolParam)) {
      const configureFormState: any = {};
      
      // Store extension info if provided (e.g., &extension=ucp)
      if (extensionParam === 'ucp') {
        const ucpExtension = 'https://ucp.dev/specification/reference?v=2026-01-11';
        configureFormState.primary_extension = ucpExtension;
        configureFormState.extensionType = 'ucp';
      }
      
      setWizardState(prev => ({
        ...prev,
        protocol: protocolParam as 'a2a' | 'ap2' | 'mcp',
        currentStep: 'configure',
        configureFormState
      }));
    }
  }, [searchParams]);

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'welcome', label: 'Choose Protocol' },
    { key: 'configure', label: 'Configure' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'complete', label: 'Complete' },
  ];
  const currentStepIndex = steps.findIndex(s => s.key === wizardState.currentStep);

  const goToStep = (step: WizardStep) => {
    setWizardState(prev => ({ ...prev, currentStep: step }));
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      goToStep(steps[nextIndex].key);
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(steps[prevIndex].key);
    }
  };

  const handleProtocolSelect = (protocol: 'a2a' | 'ap2' | 'mcp') => {
    setWizardState(prev => ({ ...prev, protocol }));
    nextStep();
  };

  const handleConfigurationComplete = (config: any, formState?: any) => {
    setWizardState(prev => ({ 
      ...prev, 
      channelConfig: config,
      configureFormState: formState || prev.configureFormState
    }));
    nextStep();
  };

  const handleChannelCreated = (config: any) => {
    setWizardState(prev => ({ ...prev, channelConfig: config }));
    nextStep();
  };

  const handleFinish = () => {
    navigate('/channels');
  };

  const handleConfigureChannel = () => {
    if (wizardState.channelConfig?.config_id) {
      navigate(`/channels/${wizardState.channelConfig.config_id}`);
    }
  };

  const handleCancel = () => {
    navigate('/channels');
  };

  // Render current step
  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 'welcome':
        return <WelcomeStep onProtocolSelect={handleProtocolSelect} />;
      case 'configure':
        return (
          <ConfigureChannelStep
            protocol={wizardState.protocol!}
            channelConfig={wizardState.channelConfig}
            configureFormState={wizardState.configureFormState}
            onConfigChange={handleConfigurationComplete}
            onBack={prevStep}
          />
        );
      case 'confirm':
        return (
          <ConfirmStep
            protocol={wizardState.protocol!}
            channelConfig={wizardState.channelConfig}
            mpxPublishConfig={null}
            onChannelCreated={handleChannelCreated}
            onBack={prevStep}
          />
        );
      case 'complete':
        return (
          <CompleteStep
            channelConfig={wizardState.channelConfig}
            onFinish={handleFinish}
            onConfigureChannel={handleConfigureChannel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-plus-circle mr-2"></i>
          Add Channel Wizard
        </h1>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleCancel}
        >
          <i className="fas fa-times mr-1"></i>
          Cancel
        </button>
      </div>

      {/* Progress Bar */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="wizard-progress">
            <div className="wizard-steps">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-step ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : ''
                  }`}
                >
                  <div className="wizard-step-circle">
                    {index < currentStepIndex ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-step-label">{step.label}</div>
                  {index < steps.length - 1 && <div className="wizard-step-line"></div>}
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div><br/>
    </div>
  );
};

export default AddChannelWizardPage;
